/**
 * PLC 仿真测试相关类型定义
 */

import type { PlcLanguageType } from './index';

// ============================================
// 变量类型
// ============================================

/** PLC 数据类型 */
export type PlcDataType = 
  | 'BOOL' 
  | 'INT' 
  | 'DINT' 
  | 'REAL' 
  | 'LREAL'
  | 'STRING'
  | 'TIME'
  | 'BYTE'
  | 'WORD'
  | 'DWORD';

/** 变量方向 */
export type VariableDirection = 'INPUT' | 'OUTPUT' | 'IN_OUT' | 'LOCAL';

/** PLC 变量定义 */
export interface PlcVariable {
  /** 变量名 */
  readonly name: string;
  /** 数据类型 */
  readonly dataType: PlcDataType;
  /** 变量方向 */
  readonly direction: VariableDirection;
  /** 初始值 */
  readonly initialValue?: string;
  /** 注释 */
  readonly comment?: string;
}

// ============================================
// 测试用例
// ============================================

/** 测试值（输入或期望输出） */
export interface TestValue {
  /** 变量名 */
  readonly variableName: string;
  /** 测试值 */
  readonly value: string;
}

/** 测试用例状态 */
export type TestCaseStatus = 'pending' | 'running' | 'passed' | 'failed' | 'error';

/** 单个测试用例 */
export interface TestCase {
  /** 用例 ID */
  readonly id: string;
  /** 用例名称 */
  readonly name: string;
  /** 用例描述 */
  readonly description?: string;
  /** 输入值列表 */
  readonly inputs: readonly TestValue[];
  /** 期望输出值列表 */
  readonly expectedOutputs: readonly TestValue[];
  /** 当前状态 */
  readonly status: TestCaseStatus;
  /** 创建时间 */
  readonly createdAt: string;
  /** 最后运行时间 */
  readonly lastRunAt?: string;
}

// ============================================
// 测试结果
// ============================================

/** 单个变量的测试结果 */
export interface VariableTestResult {
  /** 变量名 */
  readonly variableName: string;
  /** 期望值 */
  readonly expectedValue: string;
  /** 实际值 */
  readonly actualValue: string;
  /** 是否通过 */
  readonly passed: boolean;
}

/** 测试用例执行结果 */
export interface TestCaseResult {
  /** 对应的测试用例 ID */
  readonly testCaseId: string;
  /** 是否通过 */
  readonly passed: boolean;
  /** 各输出变量的结果 */
  readonly variableResults: readonly VariableTestResult[];
  /** 执行时间（毫秒） */
  readonly executionTime: number;
  /** 错误信息（如果执行出错） */
  readonly errorMessage?: string;
  /** 执行时间戳 */
  readonly timestamp: string;
}

// ============================================
// 代码解析结果
// ============================================

/** 代码解析结果 */
export interface CodeParseResult {
  /** 是否解析成功 */
  readonly success: boolean;
  /** 提取的变量列表 */
  readonly variables: readonly PlcVariable[];
  /** 输入变量 */
  readonly inputs: readonly PlcVariable[];
  /** 输出变量 */
  readonly outputs: readonly PlcVariable[];
  /** 解析错误信息 */
  readonly errors?: readonly string[];
  /** 代码语言 */
  readonly language: PlcLanguageType;
}

// ============================================
// 仿真执行
// ============================================

/** 仿真执行上下文 */
export interface SimulationContext {
  /** 变量值映射 */
  readonly variables: Record<string, unknown>;
  /** 执行的代码 */
  readonly code: string;
  /** 代码语言 */
  readonly language: PlcLanguageType;
}

/** 仿真执行结果 */
export interface SimulationResult {
  /** 是否执行成功 */
  readonly success: boolean;
  /** 执行后的变量值 */
  readonly outputValues: Record<string, unknown>;
  /** 错误信息 */
  readonly error?: string;
  /** 执行日志 */
  readonly logs?: readonly string[];
}

// ============================================
// 测试面板状态
// ============================================

/** 测试面板状态 */
export interface TestPanelState {
  /** 测试用例列表 */
  readonly testCases: readonly TestCase[];
  /** 测试结果映射 */
  readonly results: Record<string, TestCaseResult>;
  /** 当前选中的用例 ID */
  readonly selectedCaseId: string | null;
  /** 是否正在运行测试 */
  readonly isRunning: boolean;
  /** 解析出的变量 */
  readonly parsedVariables: CodeParseResult | null;
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
export const generateTestId = (): string => {
  return `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/** 创建空测试用例 */
export const createEmptyTestCase = (
  inputs: readonly PlcVariable[],
  outputs: readonly PlcVariable[]
): TestCase => ({
  id: generateTestId(),
  name: '新测试用例',
  description: '',
  inputs: inputs.map(v => ({ 
    variableName: v.name, 
    value: v.initialValue ?? getDefaultValueForType(v.dataType) 
  })),
  expectedOutputs: outputs.map(v => ({ 
    variableName: v.name, 
    value: '' 
  })),
  status: 'pending',
  createdAt: new Date().toISOString()
});

/** 获取数据类型的默认值 */
export const getDefaultValueForType = (dataType: PlcDataType): string => {
  switch (dataType) {
    case 'BOOL':
      return 'FALSE';
    case 'INT':
    case 'DINT':
    case 'BYTE':
    case 'WORD':
    case 'DWORD':
      return '0';
    case 'REAL':
    case 'LREAL':
      return '0.0';
    case 'STRING':
      return '';
    case 'TIME':
      return 'T#0s';
    default:
      return '';
  }
};